import React, { useState, useEffect, useRef } from 'react';

// Color scheme with transparency for softer appearance
const COLORS = {
  domain1: '#F59E0B',     // Orange/Amber - Sales
  domain2: '#A855F7',     // Purple - Marketing (vibrant purple that complements the theme)
  domain3: '#3B82F6',     // Blue - Customer (center)
  domain4: '#10B981',     // Green - Operations
  neutral: '#64748B',      // Gray for unclassified
};

// Get stroke opacity based on color (green and pink get more transparency)
const getColorOpacity = (color) => {
  if (color === COLORS.domain2 || color === COLORS.domain4) {
    return 0.6; // Pink and Green
  }
  return 0.8; // Others
};

// Helper to calculate circular positions around a center point (full circle)
const getOrbitPosition = (centerX, centerY, radius, angleIndex, totalItems, startAngle = -Math.PI / 2) => {
  const angle = startAngle + (2 * Math.PI * angleIndex) / totalItems;
  return {
    x: centerX + radius * Math.cos(angle),
    y: centerY + radius * Math.sin(angle),
  };
};

// Helper to calculate clustered positions for columns (limited spread)
// startAngle should point away from the domain to avoid overlap with domain-table line
const getColumnOrbitPosition = (centerX, centerY, radius, angleIndex, totalItems, startAngle = -Math.PI / 2) => {
  // Calculate appropriate spread based on number of columns (2-6)
  let spread;
  switch(totalItems) {
    case 2: spread = Math.PI * 0.4; break;   // ~72 degrees
    case 3: spread = Math.PI * 0.6; break;   // ~108 degrees
    case 4: spread = Math.PI * 0.9; break;   // ~162 degrees
    case 5: spread = Math.PI * 1.2; break;   // ~216 degrees
    case 6: spread = Math.PI * 1.5; break;   // ~270 degrees
    default: spread = Math.PI * 1.7; break;  // larger spread for more
  }
  
  const spacing = totalItems > 1 ? spread / (totalItems - 1) : 0;
  const angle = startAngle + (spacing * angleIndex) - (spread / 2);
  
  return {
    x: centerX + radius * Math.cos(angle),
    y: centerY + radius * Math.sin(angle),
  };
};

// Calculate angle from domain center to table position (used to position columns away from domain)
const getAngleFromDomain = (domainX, domainY, tableX, tableY) => {
  return Math.atan2(tableY - domainY, tableX - domainX);
};

// Domain definitions - evenly distributed across the canvas
// IMPORTANT: position must match DOMAIN_CONFIGS center for proper centering
const domainDefinitions = [
  {
    id: 'domain-marketing',
    label: 'Marketing',
    color: COLORS.domain2,
    position: { x: 590, y: 170 }, // Top right - matches DOMAIN_CONFIGS center
  },
  {
    id: 'domain-customer',
    label: 'Customer',
    color: COLORS.domain3,
    position: { x: 230, y: 300 }, // Left-center - matches DOMAIN_CONFIGS center
  },
  {
    id: 'domain-operations',
    label: 'Operations',
    color: COLORS.domain4,
    position: { x: 590, y: 460 }, // Bottom right - matches DOMAIN_CONFIGS center
  },
];

// Table definitions with their domain assignments
const tableDefinitions = [
  // Marketing domain - 3 tables
  {
    id: 'table-campaigns',
    label: 'Campaigns',
    domain: 'domain-marketing',
    columns: [
      { id: 'col-camp-id', label: 'Campaign ID', datatype: 'id' },
      { id: 'col-camp-cid', label: 'c_id', datatype: 'id' },
      { id: 'col-camp-budget', label: 'Budget', datatype: 'currency' },
      { id: 'col-camp-channel', label: 'Channel', datatype: 'text' },
      { id: 'col-camp-start', label: 'Start Date', datatype: 'date' },
    ]
  },
  {
    id: 'table-leads',
    label: 'Leads',
    domain: 'domain-marketing',
    columns: [
      { id: 'col-lead-id', label: 'Lead ID', datatype: 'id' },
      { id: 'col-lead-partyid', label: 'party_id', datatype: 'id' },
      { id: 'col-lead-source', label: 'Source', datatype: 'text' },
      { id: 'col-lead-score', label: 'Score', datatype: 'integer' },
    ]
  },
  {
    id: 'table-analytics',
    label: 'Analytics',
    domain: 'domain-marketing',
    columns: [
      { id: 'col-anlyt-id', label: 'Metric ID', datatype: 'id' },
      { id: 'col-anlyt-clicks', label: 'Clicks', datatype: 'integer' },
    ]
  },
  // Customer domain - 5 tables (center)
  {
    id: 'table-customers',
    label: 'Accounts',
    domain: 'domain-customer',
    columns: [
      { id: 'col-cust-tier', label: 'Acc ID', datatype: 'id' },
      { id: 'col-cust-name', label: 'Balance', datatype: 'currency' },
      { id: 'col-cust-email', label: 'Type', datatype: 'text' },
      { id: 'col-cust-joined', label: 'Status', datatype: 'text' },
    ]
  },
  {
    id: 'table-accounts',
    label: 'Customers',
    domain: 'domain-customer',
    columns: [
      { id: 'col-acct-id', label: 'Phone', datatype: 'text' },
      { id: 'col-acct-balance', label: 'Name', datatype: 'text' },
      { id: 'col-acct-type', label: 'Email', datatype: 'text' },
      { id: 'col-acct-custid', label: 'Cust ID', datatype: 'id' },
      { id: 'col-acct-created', label: 'Joined', datatype: 'date' },
      { id: 'col-acct-status', label: 'Tier', datatype: 'text' },
    ]
  },
  {
    id: 'table-contacts',
    label: 'Contacts',
    domain: 'domain-customer',
    columns: [
      { id: 'col-cont-id', label: 'Contact ID', datatype: 'id' },
      { id: 'col-cont-custid', label: 'cust_id', datatype: 'id' },
      { id: 'col-cont-phone', label: 'Phone', datatype: 'text' },
      { id: 'col-cont-role', label: 'Role', datatype: 'text' },
    ]
  },
  {
    id: 'table-transactions',
    label: 'Transactions',
    domain: 'domain-customer',
    columns: [
      { id: 'col-trans-id', label: 'Trans ID', datatype: 'id' },
      { id: 'col-trans-custid', label: 'cust_id', datatype: 'id' },
      { id: 'col-trans-value', label: 'Value', datatype: 'currency' },
    ]
  },
  {
    id: 'table-feedback',
    label: 'Feedback',
    domain: 'domain-customer',
    columns: [
      { id: 'col-fdbk-id', label: 'Feedback ID', datatype: 'id' },
      { id: 'col-fdbk-rating', label: 'Rating', datatype: 'integer' },
      { id: 'col-fdbk-comment', label: 'Comment', datatype: 'text' },
      { id: 'col-fdbk-submitted', label: 'Submitted', datatype: 'date' },
      { id: 'col-fdbk-category', label: 'Category', datatype: 'text' },
    ]
  },
  // Operations domain - 3 tables (bottom right)
  {
    id: 'table-inventory',
    label: 'Inventory',
    domain: 'domain-operations',
    columns: [
      { id: 'col-inv-id', label: 'Inv ID', datatype: 'id' },
      { id: 'col-inv-accid', label: 'acc_id', datatype: 'id' },
      { id: 'col-inv-stock', label: 'Stock', datatype: 'integer' },
      { id: 'col-inv-location', label: 'Location', datatype: 'text' },
    ]
  },
  {
    id: 'table-shipments',
    label: 'Shipments',
    domain: 'domain-operations',
    columns: [
      { id: 'col-ship-id', label: 'Ship ID', datatype: 'id' },
      { id: 'col-ship-ctid', label: 'ct_id', datatype: 'id' },
      { id: 'col-ship-carrier', label: 'Carrier', datatype: 'text' },
      { id: 'col-ship-date', label: 'Ship Date', datatype: 'date' },
    ]
  },
  {
    id: 'table-suppliers',
    label: 'Suppliers',
    domain: 'domain-operations',
    columns: [
      { id: 'col-supp-id', label: 'Supplier ID', datatype: 'id' },
      { id: 'col-supp-name', label: 'Name', datatype: 'text' },
    ]
  },
];

// Scattered positions for all 11 tables - distributed with maximum spread
// ViewBox: 750x600, Center: (375, 300)
// Using ellipse to maximize horizontal spread while fitting vertical bounds
// Column orbit radius: 54px
const generateCircularPositions = () => {
  const centerX = 375;
  const centerY = 300;
  const positions = [];
  
  // Outer ellipse - 7 tables (maximizing spread)
  // X has more room (750), Y is constrained (600)
  const outerRadiusX = 290;  // Wide horizontal spread
  const outerRadiusY = 190;  // Increased vertical spread
  const outerCount = 7;
  for (let i = 0; i < outerCount; i++) {
    const angle = -Math.PI / 2 + (2 * Math.PI * i) / outerCount;
    positions.push({
      x: centerX + outerRadiusX * Math.cos(angle),
      y: centerY + outerRadiusY * Math.sin(angle),
    });
  }
  
  // Inner ellipse - 4 tables with good spacing from outer
  const innerRadiusX = 130;
  const innerRadiusY = 100;  // Increased vertical spread
  const innerCount = 4;
  for (let i = 0; i < innerCount; i++) {
    // Offset by 45° so inner tables sit between outer tables
    const angle = -Math.PI / 4 + (2 * Math.PI * i) / innerCount;
    positions.push({
      x: centerX + innerRadiusX * Math.cos(angle),
      y: centerY + innerRadiusY * Math.sin(angle),
    });
  }
  
  return positions;
};

const baseScatteredPositions = generateCircularPositions();

// Table order: Campaigns, Leads, Analytics, Customers, Accounts, Contacts, Transactions, Feedback, Inventory, Shipments, Suppliers
// Apply manual adjustments for specific tables
const scatteredPositions = baseScatteredPositions.map((pos, index) => {
  // Index 1 = Leads - move right slightly
  if (index === 1) {
    return { x: pos.x + 25, y: pos.y };
  }
  // Index 3 = Customers, Index 4 = Accounts - move down
  if (index === 3 || index === 4) {
    return { x: pos.x, y: pos.y + 30 };
  }
  return pos;
});

// Domain orbit configurations - adjusted for even spacing
const DOMAIN_CONFIGS = {
  'domain-marketing': { center: { x: 590, y: 170 }, orbitRadius: 80 },
  'domain-customer': { center: { x: 230, y: 300 }, orbitRadius: 132 },
  'domain-operations': { center: { x: 590, y: 460 }, orbitRadius: 80 },
};

const COLUMN_ORBIT_RADIUS = 58;

// Generate nodes for a table at a given position
// domainCenter is optional - if provided, columns will be positioned away from the domain
const generateTableNodes = (tableDef, position, color, options = {}, domainCenter = null) => {
  const { showColumns = true, showDataTypes = true } = options;
  const nodes = [];
  const links = [];
  
  // Table node
  nodes.push({
    id: tableDef.id,
    type: 'table',
    label: tableDef.label,
    x: position.x,
    y: position.y,
    color: color,
    domain: tableDef.domain,
  });
  
  // Column nodes in orbit (only if showColumns is true)
  if (showColumns) {
    const numColumns = tableDef.columns.length;
    
    // Calculate start angle: position columns on the opposite side from domain
    let startAngle = -Math.PI / 2; // default: top
    if (domainCenter) {
      // Get angle from domain to table, then add PI to face away from domain
      const angleFromDomain = getAngleFromDomain(domainCenter.x, domainCenter.y, position.x, position.y);
      startAngle = angleFromDomain; // Columns face outward, away from domain
    }
    
    tableDef.columns.forEach((col, index) => {
      // Use clustered positioning for columns, facing away from domain
      const colPos = getColumnOrbitPosition(position.x, position.y, COLUMN_ORBIT_RADIUS, index, numColumns, startAngle);
      nodes.push({
        id: col.id,
        type: 'column',
        label: col.label,
        x: colPos.x,
        y: colPos.y,
        color: color,
        datatype: showDataTypes ? col.datatype : null,
      });
      links.push({ source: tableDef.id, target: col.id });
    });
  }
  
  return { nodes, links };
};

// Get tables belonging to a domain
const getTablesForDomain = (domainId) => {
  return tableDefinitions.filter(t => t.domain === domainId);
};

// Get domain color
const getDomainColor = (domainId) => {
  const domain = domainDefinitions.find(d => d.id === domainId);
  return domain ? domain.color : COLORS.neutral;
};

// Animation step generator
const generateSteps = () => {
  const steps = [];
  
  // Step 0-1: Show ONLY table nodes scattered (no columns)
  for (let hold = 0; hold < 2; hold++) {
    const stepNodes = [];
    const stepLinks = [];
    
    tableDefinitions.forEach((tableDef, index) => {
      const { nodes, links } = generateTableNodes(tableDef, scatteredPositions[index], COLORS.neutral, { showColumns: false });
      stepNodes.push(...nodes);
      stepLinks.push(...links);
    });
    
    steps.push({
      phase: 'tables',
      nodes: stepNodes,
      links: stepLinks,
    });
  }
  
  // Step 2: Column nodes appear (without data type icons)
  {
    const stepNodes = [];
    const stepLinks = [];
    
    tableDefinitions.forEach((tableDef, index) => {
      const { nodes, links } = generateTableNodes(tableDef, scatteredPositions[index], COLORS.neutral, { showColumns: true, showDataTypes: false });
      stepNodes.push(...nodes);
      stepLinks.push(...links);
    });
    
    steps.push({
      phase: 'columns',
      nodes: stepNodes,
      links: stepLinks,
    });
  }
  
  // Step 3: Data type icons appear in columns
  {
    const stepNodes = [];
    const stepLinks = [];
    
    tableDefinitions.forEach((tableDef, index) => {
      const { nodes, links } = generateTableNodes(tableDef, scatteredPositions[index], COLORS.neutral, { showColumns: true, showDataTypes: true });
      stepNodes.push(...nodes);
      stepLinks.push(...links);
    });
    
    steps.push({
      phase: 'datatypes',
      nodes: stepNodes,
      links: stepLinks,
    });
  }
  
  // Step 4: Hold with all elements visible before gathering
  {
    const stepNodes = [];
    const stepLinks = [];
    
    tableDefinitions.forEach((tableDef, index) => {
      const { nodes, links } = generateTableNodes(tableDef, scatteredPositions[index], COLORS.neutral);
      stepNodes.push(...nodes);
      stepLinks.push(...links);
    });
    
    steps.push({
      phase: 'scattered',
      nodes: stepNodes,
      links: stepLinks,
    });
  }
  
  // Step 5: ALL tables move SIMULTANEOUSLY to their domain orbit positions
  {
    const stepNodes = [];
    const stepLinks = [];
    
    domainDefinitions.forEach(domain => {
      const domainTables = getTablesForDomain(domain.id);
      const config = DOMAIN_CONFIGS[domain.id];
      
      domainTables.forEach((tableDef, index) => {
        const position = getOrbitPosition(config.center.x, config.center.y, config.orbitRadius, index, domainTables.length);
        const { nodes, links } = generateTableNodes(tableDef, position, domain.color, {}, config.center);
        stepNodes.push(...nodes);
        stepLinks.push(...links);
      });
    });
    
    steps.push({
      phase: 'gathering',
      nodes: stepNodes,
      links: stepLinks,
    });
  }
  
  // Step 6: Hold in orbit position before domains appear
  {
    const stepNodes = [];
    const stepLinks = [];
    
    domainDefinitions.forEach(domain => {
      const domainTables = getTablesForDomain(domain.id);
      const config = DOMAIN_CONFIGS[domain.id];
      
      domainTables.forEach((tableDef, index) => {
        const position = getOrbitPosition(config.center.x, config.center.y, config.orbitRadius, index, domainTables.length);
        const { nodes, links } = generateTableNodes(tableDef, position, domain.color, {}, config.center);
        stepNodes.push(...nodes);
        stepLinks.push(...links);
      });
    });
    
    steps.push({
      phase: 'gathered',
      nodes: stepNodes,
      links: stepLinks,
    });
  }
  
  // Step 7: All domain nodes appear at their centers
  {
    const stepNodes = [];
    const stepLinks = [];
    
    // Add all domain nodes
    domainDefinitions.forEach(domain => {
      stepNodes.push({
        id: domain.id,
        type: 'domain',
        label: domain.label,
        x: domain.position.x,
        y: domain.position.y,
        color: domain.color,
      });
      
      const domainTables = getTablesForDomain(domain.id);
      const config = DOMAIN_CONFIGS[domain.id];
      
      domainTables.forEach((tableDef, index) => {
        const position = getOrbitPosition(config.center.x, config.center.y, config.orbitRadius, index, domainTables.length);
        const { nodes, links } = generateTableNodes(tableDef, position, domain.color, {}, config.center);
        stepNodes.push(...nodes);
        stepLinks.push(...links);
        
        // Link domain to table
        stepLinks.push({ source: domain.id, target: tableDef.id, type: 'domain' });
      });
    });
    
    steps.push({
      phase: 'classified',
      nodes: stepNodes,
      links: stepLinks,
    });
  }
  
  // Step 8: Show cross-domain relationships
  {
    const stepNodes = [];
    const stepLinks = [];
    
    // Add all domain nodes
    domainDefinitions.forEach(domain => {
      stepNodes.push({
        id: domain.id,
        type: 'domain',
        label: domain.label,
        x: domain.position.x,
        y: domain.position.y,
        color: domain.color,
      });
      
      const domainTables = getTablesForDomain(domain.id);
      const config = DOMAIN_CONFIGS[domain.id];
      
      domainTables.forEach((tableDef, index) => {
        const position = getOrbitPosition(config.center.x, config.center.y, config.orbitRadius, index, domainTables.length);
        const { nodes, links } = generateTableNodes(tableDef, position, domain.color, {}, config.center);
        stepNodes.push(...nodes);
        stepLinks.push(...links);
        stepLinks.push({ source: domain.id, target: tableDef.id, type: 'domain' });
      });
    });
    
    // Cross-domain relationships - cust_id in Accounts connecting to multiple tables in other domains
    // Customer domain cust_id -> Marketing domain c_id (Campaigns)
    stepLinks.push({ source: 'col-acct-custid', target: 'col-camp-cid', type: 'relationship' });
    // Customer domain cust_id -> Marketing domain party_id (Leads)
    stepLinks.push({ source: 'col-acct-custid', target: 'col-lead-partyid', type: 'relationship' });
    // Customer domain cust_id -> Operations domain acc_id (Inventory)
    stepLinks.push({ source: 'col-acct-custid', target: 'col-inv-accid', type: 'relationship' });
    // Customer domain cust_id -> Operations domain ct_id (Shipments)
    stepLinks.push({ source: 'col-acct-custid', target: 'col-ship-ctid', type: 'relationship' });
    // Within Customer domain - cust_id -> Contacts cust_id
    stepLinks.push({ source: 'col-acct-custid', target: 'col-cont-custid', type: 'relationship' });
    // Within Customer domain - cust_id -> Transactions cust_id
    stepLinks.push({ source: 'col-acct-custid', target: 'col-trans-custid', type: 'relationship' });
    // Within Customer domain - Customers cust_id -> Accounts acc_id
    stepLinks.push({ source: 'col-acct-custid', target: 'col-cust-tier', type: 'relationship' });
    
    steps.push({
      phase: 'relationships',
      nodes: stepNodes,
      links: stepLinks,
    });
  }
  
  return steps;
};

const graphSteps = generateSteps();

// Define relationship pairs for sequential highlighting
const RELATIONSHIP_PAIRS = [
  { source: 'col-acct-custid', target: 'col-camp-cid' },
  { source: 'col-acct-custid', target: 'col-lead-partyid' },
  { source: 'col-acct-custid', target: 'col-inv-accid' },
  { source: 'col-acct-custid', target: 'col-ship-ctid' },
  { source: 'col-acct-custid', target: 'col-cont-custid' },
  { source: 'col-acct-custid', target: 'col-trans-custid' },
  { source: 'col-acct-custid', target: 'col-cust-tier' },
];

const AnimatedSemanticGraph = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [revealedRelationships, setRevealedRelationships] = useState(0);
  const [showFullGraph, setShowFullGraph] = useState(false);
  const svgRef = useRef(null);

  // Auto-advance with phase-specific timing for smooth experience
  useEffect(() => {
    const currentPhase = graphSteps[currentStep]?.phase;
    
    // Phase-specific delays for smoother, consistent animation
    const getDelay = () => {
      switch(currentPhase) {
        case 'tables':        return 2000;  // Initial table discovery
        case 'columns':       return 2000;  // Columns appearing
        case 'datatypes':     return 2500;  // Data types with pop-in effect
        case 'scattered':     return 1500;  // Brief hold before movement
        case 'gathering':     return 2500;  // Movement animation (matches CSS transition)
        case 'gathered':      return 1500;  // Brief hold after movement
        case 'classified':    return 2500;  // Domain nodes appear
        case 'relationships': return 20000;  // Extended for sequential relationship reveal + 4s extra at end
        default:              return 2000;
      }
    };
    
    const timeout = setTimeout(() => {
      setCurrentStep((prev) => (prev + 1) % graphSteps.length);
      // Reset relationship state when cycling back
      if (currentStep === graphSteps.length - 1) {
        setRevealedRelationships(0);
        setShowFullGraph(false);
      }
    }, getDelay());
    
    return () => clearTimeout(timeout);
  }, [currentStep]);

  // Progressive relationship reveal during relationships phase
  useEffect(() => {
    const currentPhase = graphSteps[currentStep]?.phase;
    
    if (currentPhase !== 'relationships') return;
    
    // Start reveal sequence after a short delay when entering relationships phase
    if (revealedRelationships === 0 && !showFullGraph) {
      const startTimer = setTimeout(() => {
        setRevealedRelationships(1);
      }, 100);
      return () => clearTimeout(startTimer);
    }
    
    // Reveal next relationship every 1.5 seconds
    if (revealedRelationships > 0 && revealedRelationships < RELATIONSHIP_PAIRS.length) {
      const timer = setTimeout(() => {
        setRevealedRelationships(prev => prev + 1);
      }, 1500);
      return () => clearTimeout(timer);
    }
    
    // After all relationships revealed, wait 3 seconds then show full graph
    if (revealedRelationships === RELATIONSHIP_PAIRS.length && !showFullGraph) {
      const timer = setTimeout(() => {
        setShowFullGraph(true);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [currentStep, revealedRelationships, showFullGraph]);

  const currentData = graphSteps[currentStep];
  const isRelationshipPhase = currentData.phase === 'relationships';
  
  // Get highlighted node IDs based on revealed relationships
  const getHighlightedNodeIds = () => {
    if (!isRelationshipPhase || showFullGraph) return null; // null means show all
    
    const highlightedIds = new Set();
    for (let i = 0; i < revealedRelationships; i++) {
      const rel = RELATIONSHIP_PAIRS[i];
      if (rel) {
        highlightedIds.add(rel.source);
        highlightedIds.add(rel.target);
      }
    }
    return highlightedIds;
  };
  
  const highlightedNodeIds = getHighlightedNodeIds();
  
  // Check if a node should be highlighted (or greyed out)
  const isNodeHighlighted = (nodeId) => {
    if (!highlightedNodeIds) return true; // Show all if not in relationship phase or full graph shown
    return highlightedNodeIds.has(nodeId);
  };
  
  // Check if a relationship link should be shown
  const isRelationshipRevealed = (source, target) => {
    if (!isRelationshipPhase) return false;
    if (showFullGraph) return true;
    
    for (let i = 0; i < revealedRelationships; i++) {
      const rel = RELATIONSHIP_PAIRS[i];
      if (rel && rel.source === source && rel.target === target) {
        return true;
      }
    }
    return false;
  };
  
  // Check if a node is part of any relationship (for glow effect at final stage)
  const isRelationshipNode = (nodeId) => {
    return RELATIONSHIP_PAIRS.some(rel => rel.source === nodeId || rel.target === nodeId);
  };
  
  const getNodePosition = (nodeId) => {
    const node = currentData.nodes.find(n => n.id === nodeId);
    return node ? { x: node.x, y: node.y } : { x: 0, y: 0 };
  };

  const getNodeSize = (type) => {
    switch (type) {
      case 'domain': return 34;  // Increased from 30
      case 'table': return 28;   // Increased from 24
      case 'column': return 22;  // Increased from 18
      default: return 16;
    }
  };

  // Check if node is new (only domain can be truly new)
  const isNewNode = (nodeId) => {
    if (currentStep === 0) return false; // Nothing is new on first render
    const prevData = graphSteps[currentStep - 1];
    return !prevData.nodes.find(n => n.id === nodeId);
  };

  // Domain icon (4x4 grid) - smaller
  const DomainIcon = ({ color }) => (
    <g>
      <rect x="-6" y="-6" width="5" height="5" fill={color} rx="1" />
      <rect x="1" y="-6" width="5" height="5" fill={color} rx="1" />
      <rect x="-6" y="1" width="5" height="5" fill={color} rx="1" />
      <rect x="1" y="1" width="5" height="5" fill={color} rx="1" />
    </g>
  );

  // Table icon - smaller
  const TableIcon = ({ color }) => (
    <g>
      <rect x="-6" y="-6" width="12" height="3" fill={color} rx="0.5" />
      <rect x="-6" y="-1.5" width="12" height="2.5" fill={color} opacity="0.7" rx="0.5" />
      <rect x="-6" y="2.5" width="12" height="2.5" fill={color} opacity="0.5" rx="0.5" />
    </g>
  );

  // Data type icons as SVG paths for consistency - using domain colors
  const DataTypeIcon = ({ datatype, color }) => {
    const iconColor = color || '#FFFFFF';
    switch(datatype) {
      case 'currency':
        return (
          <text y={3} textAnchor="middle" fill={iconColor} fontSize={10} fontWeight="bold" style={{ fontFamily: 'Arial, sans-serif' }}>$</text>
        );
      case 'integer':
        return (
          <text y={3} textAnchor="middle" fill={iconColor} fontSize={10} fontWeight="bold" style={{ fontFamily: 'Arial, sans-serif' }}>#</text>
        );
      case 'id':
        return (
          <g transform="translate(-4, -4)">
            <circle cx="3" cy="3" r="2" fill="none" stroke={iconColor} strokeWidth="1.2" />
            <line x1="4.5" y1="4.5" x2="8" y2="8" stroke={iconColor} strokeWidth="1.5" />
            <line x1="6" y1="8" x2="8" y2="6" stroke={iconColor} strokeWidth="1.2" />
          </g>
        );
      case 'text':
        return (
          <text y={3} textAnchor="middle" fill={iconColor} fontSize={10} fontWeight="bold" style={{ fontFamily: 'Arial, sans-serif' }}>T</text>
        );
      case 'date':
        return (
          <g transform="translate(-5, -5)">
            <rect x="1" y="1.5" width="8" height="7" rx="0.5" fill="none" stroke={iconColor} strokeWidth="1" />
            <line x1="1" y1="4" x2="9" y2="4" stroke={iconColor} strokeWidth="0.8" />
            <line x1="3" y1="0" x2="3" y2="2.5" stroke={iconColor} strokeWidth="1" />
            <line x1="7" y1="0" x2="7" y2="2.5" stroke={iconColor} strokeWidth="1" />
          </g>
        );
      default:
        return (<circle r={2} fill={iconColor} />);
    }
  };

  const getPhaseText = () => {
    switch(currentData.phase) {
      case 'tables': return 'organizing_data';
      case 'columns': return 'organizing_data';
      case 'datatypes': return 'generating_data_profile';
      case 'scattered': return 'generating_data_profile';
      case 'gathering': return 'classifying_domains';
      case 'gathered': return 'classifying_domains';
      case 'classified': return 'classifying_domains';
      case 'relationships': 
        if (showFullGraph) {
          return '✓ semantic_graph';
        }
        return 'identifying_relationships';
      default: return '';
    }
  };
  
  const getStepNumber = () => {
    const phaseOrder = ['tables', 'columns', 'datatypes', 'scattered', 'gathering', 'gathered', 'classified', 'relationships'];
    const currentPhaseIndex = phaseOrder.indexOf(currentData.phase);
    return currentPhaseIndex + 1;
  };
  
  const totalPhases = 8;

  return (
    <div className="w-full h-full relative overflow-hidden rounded-lg">
      {/* Background */}
      <div 
        className="absolute inset-0 bg-slate-900/50 rounded-lg"
      >
        <div 
          className="absolute inset-0"
          style={{
            backgroundImage: `
              linear-gradient(rgba(59, 130, 246, 0.04) 1px, transparent 1px),
              linear-gradient(90deg, rgba(59, 130, 246, 0.04) 1px, transparent 1px)
            `,
            backgroundSize: '20px 20px',
            maskImage: 'radial-gradient(ellipse at center, black 30%, transparent 75%)',
            WebkitMaskImage: 'radial-gradient(ellipse at center, black 30%, transparent 75%)',
          }}
        />
      </div>

      {/* SVG Graph */}
      <svg 
        ref={svgRef}
        className="w-full h-full relative z-10"
        viewBox="0 0 750 600"
        preserveAspectRatio="xMidYMid meet"
      >
        <defs>
          <filter id="glow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
            <feMerge>
              <feMergeNode in="coloredBlur"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
          
          <filter id="domain-glow" x="-100%" y="-100%" width="300%" height="300%">
            <feGaussianBlur stdDeviation="6" result="coloredBlur"/>
            <feMerge>
              <feMergeNode in="coloredBlur"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
        </defs>

        {/* Guides during gathering phase */}
        {currentData.phase === 'gathering' && domainDefinitions.map(domain => {
          const config = DOMAIN_CONFIGS[domain.id];
          return (
            <circle
              key={`guide-${domain.id}`}
              cx={config.center.x}
              cy={config.center.y}
              r={config.orbitRadius + 15}
              fill="none"
              stroke={`${domain.color}25`}
              strokeWidth="1"
              strokeDasharray="6,6"
            />
          );
        })}

        {/* Links */}
        <g className="links">
          {currentData.links.map((link, index) => {
            const sourcePos = getNodePosition(link.source);
            const targetPos = getNodePosition(link.target);
            const linkId = `${link.source}-${link.target}`;
            const isRelationship = link.type === 'relationship';
            const isDomain = link.type === 'domain';
            
            const sourceNode = currentData.nodes.find(n => n.id === link.source);
            
            // Calculate relationship index for staggered animation
            const relationshipLinks = currentData.links.filter(l => l.type === 'relationship');
            const relationshipIndex = relationshipLinks.findIndex(l => 
              `${l.source}-${l.target}` === linkId
            );
            
            let strokeColor = 'rgba(100, 116, 139, 0.2)';
            let strokeWidth = 1;
            let linkOpacity = 0.25;
            
            // During relationship phase, grey out non-highlighted links
            const shouldGreyOut = isRelationshipPhase && !showFullGraph && highlightedNodeIds;
            
            if (isRelationship) {
              // Check if this relationship should be shown
              const isRevealed = isRelationshipRevealed(link.source, link.target);
              if (!isRevealed) {
                return null; // Don't render unrevealed relationships
              }
              strokeColor = '#94A3B8'; // Silver grey for relationship lines
              strokeWidth = showFullGraph ? 0.4 : 0.8; // Thinner when full graph shown
              linkOpacity = showFullGraph ? 0.6 : 1;
            } else if (isDomain) {
              strokeColor = `${sourceNode?.color}50`;
              strokeWidth = 2;
              linkOpacity = shouldGreyOut ? 0.1 : 0.4;
            } else {
              // Table to column links
              linkOpacity = shouldGreyOut ? 0.05 : 0.25;
            }
            
            return (
              <line
                key={linkId}
                x1={sourcePos.x}
                y1={sourcePos.y}
                x2={targetPos.x}
                y2={targetPos.y}
                stroke={strokeColor}
                strokeWidth={strokeWidth}
                strokeDasharray={isRelationship ? '4,2' : 'none'}
                className={`transition-all duration-500 ${isRelationship ? 'relationship-flow' : ''}`}
                style={{ 
                  opacity: linkOpacity,
                }}
              />
            );
          })}
        </g>

        {/* Nodes */}
        <g className="nodes">
          {currentData.nodes.map((node) => {
            const size = getNodeSize(node.type);
            const isNew = isNewNode(node.id);
            const isDomain = node.type === 'domain';
            const isTable = node.type === 'table';
            const isColumn = node.type === 'column';
            
            // Check if this table is currently moving
            const isGathering = currentData.phase === 'gathering';
            
            // Check if node should be highlighted or greyed out
            const shouldGreyOut = isRelationshipPhase && !showFullGraph && highlightedNodeIds && !isNodeHighlighted(node.id);
            const nodeOpacityMultiplier = shouldGreyOut ? 0.08 : 1;
            
            // Domain nodes use SVG transform (no transition needed)
            // Other nodes use CSS transform for smooth transitions
            if (isDomain) {
              return (
                <g 
                  key={node.id}
                  transform={`translate(${node.x}, ${node.y})`}
                  className="transition-opacity duration-500"
                  style={{ opacity: nodeOpacityMultiplier }}
                >
                  <g
                    style={{
                      opacity: isNew ? 0 : 1,
                      animation: isNew ? 'domainAppear 0.6s ease-out forwards' : 'none',
                    }}
                  >
                    {/* Glow ring for domain */}
                    <circle
                      r={size / 2 + 4}
                      fill="none"
                      stroke={node.color}
                      strokeWidth={1.5}
                      opacity={0.2 * getColorOpacity(node.color)}
                      filter="url(#domain-glow)"
                    />
                    <circle
                      r={size / 2 + 2}
                      fill="none"
                      stroke={node.color}
                      strokeWidth={1}
                      opacity={0.4 * getColorOpacity(node.color)}
                    />
                    
                    {/* Main node circle */}
                    <circle
                      r={size / 2}
                      fill="#0F172A"
                      stroke={node.color}
                      strokeWidth={2}
                      strokeOpacity={getColorOpacity(node.color)}
                      filter={showFullGraph ? 'none' : 'url(#glow)'}
                    />
                    
                    {/* Inner ring */}
                    <circle
                      r={size / 2 - 4}
                      fill="none"
                      stroke={node.color}
                      strokeWidth={1}
                      opacity={0.5 * getColorOpacity(node.color)}
                    />

                    <DomainIcon color={node.color} />
                    
                    <text
                      y={size / 2 + 14}
                      textAnchor="middle"
                      fill="#E2E8F0"
                      fontSize={9}
                      fontWeight={700}
                      style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}
                    >
                      {node.label}
                    </text>
                  </g>
                </g>
              );
            }
            
            // Non-domain nodes use CSS transform for smooth movement
            return (
              <g 
                key={node.id}
                className="node-transition transition-opacity duration-500"
                style={{
                  transform: `translate(${node.x}px, ${node.y}px)`,
                  opacity: isNew ? 0 : nodeOpacityMultiplier,
                  animation: isNew ? 'nodeAppear 0.5s ease-out forwards' : 'none',
                }}
              >
                {/* Main node circle */}
                <circle
                  r={size / 2}
                  fill="#0F172A"
                  stroke={node.color}
                  strokeWidth={isTable ? 2 : 1.2}
                  strokeOpacity={getColorOpacity(node.color)}
                  filter={showFullGraph && !isRelationshipNode(node.id) ? 'none' : 'url(#glow)'}
                  className={showFullGraph && !isRelationshipNode(node.id) ? '' : 'node-glow-pulse'}
                />

                {/* Icons */}
                {isTable && <TableIcon color={node.color} />}
                
                {/* Column data type */}
                {isColumn && node.datatype && (
                  <g className={currentData.phase === 'datatypes' ? 'datatype-icon-animate' : 'datatype-icon'}>
                    <DataTypeIcon datatype={node.datatype} color={node.color} />
                  </g>
                )}
                
                {/* Label */}
                <text
                  y={size / 2 + (isTable ? 14 : 10)}
                  textAnchor="middle"
                  fill="#E2E8F0"
                  fontSize={isTable ? 10 : 8}
                  fontWeight={isTable ? 600 : 400}
                  style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}
                >
                  {node.label.length > 12 ? node.label.substring(0, 10) + '...' : node.label}
                </text>
              </g>
            );
          })}
        </g>
      </svg>

      {/* Phase indicator - styled like graph section */}
      <div className="absolute top-1 left-3 px-2 py-1.5">
        <span className="text-xs text-blue-400 font-mono tracking-wide">
          {getPhaseText()}{(currentData.phase !== 'relationships' || !showFullGraph) && <span className="loading-dots"></span>}
        </span>
      </div>

      {/* CSS */}
      <style>{`
        .node-transition {
          transition: transform 1.5s ease-in-out, opacity 0.8s ease-out;
        }
        
        @keyframes nodeAppear {
          0% {
            opacity: 0;
          }
          100% {
            opacity: 1;
          }
        }
        
        @keyframes domainAppear {
          0% {
            opacity: 0;
          }
          100% {
            opacity: 1;
          }
        }
        
        .datatype-icon {
          opacity: 1;
        }
        
        .datatype-icon-animate {
          animation: datatypeAppear 1.2s ease-out forwards, datatypeGlow 1.5s ease-out forwards;
        }
        
        @keyframes datatypeAppear {
          0% {
            opacity: 0;
            transform: scale(0);
          }
          40% {
            opacity: 1;
            transform: scale(1.5);
          }
          70% {
            transform: scale(0.9);
          }
          100% {
            opacity: 1;
            transform: scale(1);
          }
        }
        
        @keyframes datatypeGlow {
          0% {
            filter: brightness(1) drop-shadow(0 0 0px white);
          }
          30% {
            filter: brightness(2) drop-shadow(0 0 8px white);
          }
          100% {
            filter: brightness(1) drop-shadow(0 0 0px white);
          }
        }
        
        .node-glow-pulse {
          animation: nodeGlowPulse 2.5s ease-in-out infinite;
        }
        
        @keyframes nodeGlowPulse {
          0%, 100% {
            opacity: 0.6;
            stroke-width: 1.5;
          }
          50% {
            opacity: 1;
            stroke-width: 2.5;
          }
        }
        
        .relationship-flow {
          animation: flowingDash 1.5s linear infinite;
        }
        
        .relationship-appear {
          animation: relationshipAppear 0.6s ease-out forwards, flowingDash 1.5s linear infinite 0.6s;
        }
        
        @keyframes relationshipAppear {
          0% {
            opacity: 0;
            stroke-dashoffset: 50;
          }
          100% {
            opacity: 0.9;
            stroke-dashoffset: 0;
          }
        }
        
        @keyframes flowingDash {
          0% {
            stroke-dashoffset: 0;
          }
          100% {
            stroke-dashoffset: -12;
          }
        }
        
        .loading-dots::after {
          content: '';
          animation: loadingDots 1.5s steps(4, end) infinite;
        }
        
        @keyframes loadingDots {
          0% {
            content: '';
          }
          25% {
            content: ' .';
          }
          50% {
            content: ' ..';
          }
          75% {
            content: ' ...';
          }
          100% {
            content: '';
          }
        }
      `}</style>
    </div>
  );
};

export default AnimatedSemanticGraph;
